import { useState } from "react";
import { automationSchema, type AutomationPayload } from "@anton/shared";
import { PageHeader } from "../components/PageHeader";
import { Field } from "../components/Field";
import { SubmitButton } from "../components/SubmitButton";
import { SuccessScreen } from "../components/SuccessScreen";
import { Card, Lead, P, H3, CheckList, SectionHeader } from "../components/Section";
import { submitApplication } from "../lib/api";
import { haptic, showAlert } from "../lib/telegram";

export default function Automation() {
  const [form, setForm] = useState<Partial<AutomationPayload>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (done) return <SuccessScreen message="Заявка пришла мне лично. Свяжусь и подключу команду." />;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = automationSchema.safeParse(form);
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      for (const i of parsed.error.issues) fe[i.path.join(".")] = i.message;
      setErrors(fe);
      haptic("error");
      return;
    }
    setLoading(true);
    const res = await submitApplication("AUTOMATION", parsed.data);
    setLoading(false);
    if (!res.ok) { showAlert(res.error ?? "Ошибка"); haptic("error"); return; }
    setDone(true);
  }

  return (
    <div className="pb-10">
      <PageHeader
        eyebrow="Автоматизация бизнеса"
        title="Превращаем твой процесс в работающий инструмент"
        subtitle="С моей командой берём конкретный процесс и упаковываем его в чат-бота, AI-ассистента или собственную систему."
        icon="🤖"
        iconColor="purple"
      />

      <div className="px-5 mt-3 space-y-3">
        <Card>
          <Lead>
            Любой повторяющийся процесс в бизнесе можно автоматизировать.
            Я с командой разработки беру конкретную задачу и упаковываю её в работающий инструмент.
          </Lead>
        </Card>

        <Card>
          <H3>Подход — 4 шага</H3>
          <CheckList items={[
            "Изучаем твой процесс «как есть» — без воды и красивых презентаций",
            "Описываем структуру: шаги, ответственные, входы/выходы",
            "Превращаем в техническую реализацию — бот, ассистент, кастомный интерфейс",
            "Внедряем у тебя и обучаем команду — чтобы система реально работала, а не пылилась",
          ]} />
        </Card>

        <Card>
          <H3>Что мы уже делали</H3>
          <div className="space-y-3 mt-2">
            <div className="ios-callout-muted">
              <b>🤝 Онбординг сотрудников через Telegram-бота</b><br/>
              32 шага: подписание NDA, корп. почта, регламенты с тестированием, знакомство с командой, настройка Todoist, видеокружок-знакомство, проверка руководителем, напоминания о ТЕТ и испытательном сроке.
            </div>
            <div className="ios-callout-muted">
              <b>📊 AI-ассистент для сбора отчётов</b><br/>
              Бот сам инициирует, спрашивает отчёт у сотрудников по расписанию, агрегирует данные руководителю.
            </div>
            <div className="ios-callout-muted">
              <b>💰 Кастомная система финучёта</b><br/>
              Собственный интерфейс под учёт и аналитику финансов в бизнесе клиента — под его процессы.
            </div>
          </div>
        </Card>

        <Card>
          <H3>Кому подойдёт</H3>
          <CheckList items={[
            "В бизнесе есть повторяющиеся ресурсоёмкие процессы",
            "Готов выделить бюджет на разработку",
            "Понимаешь, что хочешь автоматизировать (или готов пройти этап описания процессов с моей командой)",
          ]} />
        </Card>

        <div className="ios-callout-muted">
          Стоимость рассчитываем под конкретный проект. Уточним на первом созвоне.
        </div>
      </div>

      <SectionHeader>Оставить заявку</SectionHeader>
      <form noValidate onSubmit={onSubmit} className="px-5 space-y-4">
        <Field label="Имя" error={errors["name"]}>
          <input className={`ios-input ${errors["name"] ? "ios-input-error" : ""}`}
            value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Контакт" error={errors["contact"]}>
          <input className={`ios-input ${errors["contact"] ? "ios-input-error" : ""}`}
            value={form.contact ?? ""} onChange={(e) => setForm({ ...form, contact: e.target.value })} />
        </Field>
        <Field label="Какой процесс хочешь автоматизировать?" error={errors["processDescription"]}>
          <textarea rows={4} className={`ios-input ${errors["processDescription"] ? "ios-input-error" : ""}`}
            value={form.processDescription ?? ""}
            onChange={(e) => setForm({ ...form, processDescription: e.target.value })}
            placeholder="Опиши процесс «как есть»" />
        </Field>
        <Field label="Какой результат ожидаешь?" error={errors["expectedResult"]}>
          <textarea rows={3} className={`ios-input ${errors["expectedResult"] ? "ios-input-error" : ""}`}
            value={form.expectedResult ?? ""}
            onChange={(e) => setForm({ ...form, expectedResult: e.target.value })}
            placeholder="что должно стать после автоматизации?" />
        </Field>
        <Field label="Желательный срок" error={errors["deadline"]}>
          <input className={`ios-input ${errors["deadline"] ? "ios-input-error" : ""}`}
            value={form.deadline ?? ""} onChange={(e) => setForm({ ...form, deadline: e.target.value })}
            placeholder="например: 2 месяца" />
        </Field>
        <div className="pt-2">
          <SubmitButton loading={loading}>Отправить заявку</SubmitButton>
        </div>
      </form>
    </div>
  );
}
